import React, { useState, useEffect, useMemo } from 'react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useCompanyScope } from '../context/CompanyScopeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Plus, Building, Edit, Trash2, Users, User } from 'lucide-react';
import { toast } from 'sonner';

export default function Departments() {
  const { user } = useAuth();
  const { selectedCompanyId, isCrossCompany } = useCompanyScope();
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    head_id: ''
  });

  useEffect(() => {
    fetchDepartments();
    fetchEmployees();
  }, []);

  const fetchDepartments = async () => {
    try {
      const response = await api.get('/departments');
      setDepartments(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des directions');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await api.get('/employees');
      setEmployees(response.data);
    } catch (error) {
      console.error('Erreur chargement employés:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        head_id: formData.head_id || null
      };

      if (editMode) {
        await api.put(`/departments/${selectedDepartment.id}`, payload);
        toast.success('Direction modifiée avec succès');
      } else {
        await api.post('/departments', payload);
        toast.success('Direction créée avec succès');
      }
      setOpen(false);
      resetForm();
      fetchDepartments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Une erreur est survenue');
    }
  };

  const handleEdit = (department) => {
    setEditMode(true);
    setSelectedDepartment(department);
    setFormData({
      name: department.name,
      description: department.description || '',
      head_id: department.head_id || ''
    });
    setOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette direction?')) {
      try {
        await api.delete(`/departments/${id}`);
        toast.success('Direction supprimée avec succès');
        fetchDepartments();
      } catch (error) {
        toast.error('Erreur lors de la suppression');
      }
    }
  };

  const resetForm = () => {
    setFormData({ name: '', description: '', head_id: '' });
    setEditMode(false);
    setSelectedDepartment(null);
  };

  const getEmployeeCount = (deptId) => {
    return employees.filter(e => e.department_id === deptId).length;
  };

  const getHeadName = (headId) => {
    const head = employees.find(e => e.id === headId);
    return head ? `${head.first_name} ${head.last_name}` : '-';
  };

  // Filtre par entreprise sélectionnée
  const scopedDepartments = useMemo(() => {
    if (!isCrossCompany || !selectedCompanyId) return departments;
    return departments.filter(d => d.company_id === selectedCompanyId);
  }, [departments, selectedCompanyId, isCrossCompany]);

  const scopedEmployees = useMemo(() => {
    if (!isCrossCompany || !selectedCompanyId) return employees;
    return employees.filter(e => e.company_id === selectedCompanyId);
  }, [employees, selectedCompanyId, isCrossCompany]);

  if (loading) {
    return <div className="text-center py-8">Chargement...</div>;
  }

  return (
    <div className="space-y-6" data-testid="departments-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-3xl text-slate-900">Directions</h1>
          <p className="text-slate-600 mt-1">Gestion des directions et départements</p>
        </div>
        <Dialog open={open} onOpenChange={(isOpen) => { setOpen(isOpen); if (!isOpen) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-primary hover:bg-primary-700" data-testid="add-department-button">
              <Plus className="w-4 h-4 mr-2" />
              Nouvelle Direction
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{editMode ? 'Modifier' : 'Nouvelle'} Direction</DialogTitle>
              <DialogDescription>
                {editMode ? 'Modifiez les informations' : 'Créez une nouvelle direction'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nom de la direction *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="Ex: Direction Commerciale"
                  data-testid="department-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Description de la direction..."
                  rows={3}
                  data-testid="department-description-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="head_id">Responsable de direction</Label>
                <select
                  id="head_id"
                  value={formData.head_id}
                  onChange={(e) => setFormData({ ...formData, head_id: e.target.value })}
                  className="w-full rounded-md border border-slate-300 p-2"
                  data-testid="department-head-select"
                >
                  <option value="">-- Sélectionner --</option>
                  {scopedEmployees.map((emp) => (
                    <option key={emp.id} value={emp.id}>
                      {emp.first_name} {emp.last_name} ({emp.position})
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setOpen(false)} className="flex-1">
                  Annuler
                </Button>
                <Button type="submit" className="flex-1 bg-primary" data-testid="submit-department-button">
                  {editMode ? 'Modifier' : 'Créer'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {scopedDepartments.length === 0 ? (
          <Card className="col-span-full">
            <CardContent className="text-center py-8 text-slate-500">
              Aucune direction trouvée. Créez votre première direction.
            </CardContent>
          </Card>
        ) : (
          scopedDepartments.map((dept) => (
            <Card key={dept.id} className="border-slate-200 hover:shadow-md transition-shadow">
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Building className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">{dept.name}</CardTitle>
                      {dept.description && (
                        <p className="text-sm text-slate-500 mt-1">{dept.description}</p>
                      )}
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm">
                    <Users className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-600">
                      {getEmployeeCount(dept.id)} employé(s)
                    </span>
                  </div>
                  {dept.head_id && (
                    <div className="flex items-center gap-2 text-sm">
                      <User className="w-4 h-4 text-slate-400" />
                      <span className="text-slate-600">
                        Chef: {getHeadName(dept.head_id)}
                      </span>
                    </div>
                  )}
                  <div className="flex gap-2 pt-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEdit(dept)}
                      className="flex-1"
                      data-testid={`edit-dept-${dept.id}`}
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      Modifier
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-error hover:bg-red-50"
                      onClick={() => handleDelete(dept.id)}
                      data-testid={`delete-dept-${dept.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}
